import { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { IdCard, Pencil, Plus, Printer, Trash2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import DataTable from "../components/common/DataTable";
import PasswordInput from "../components/common/PasswordInput";
import ProfilePhotoInput from "../components/common/ProfilePhotoInput";
import { erpApi, formatCurrency } from "../services/erpApi";
import { CLINIC_NAME } from "../config/branding";

const emptyForm = {
  name: "",
  role: "",
  department: "",
  photo_url: "",
  joining_date: "",
  salary: "",
  phone: "",
  working_days: ["mon", "tue", "wed", "thu", "fri", "sat"],
  shift_start: "09:00",
  shift_end: "18:00",
  grace_minutes: "15",
  full_day_hours: "8",
  half_day_hours: "4",
  create_login: false,
  username: "",
  password: "",
};

const weekDays = [
  { value: "mon", label: "Mon" },
  { value: "tue", label: "Tue" },
  { value: "wed", label: "Wed" },
  { value: "thu", label: "Thu" },
  { value: "fri", label: "Fri" },
  { value: "sat", label: "Sat" },
  { value: "sun", label: "Sun" },
];

const employeeRoles = [
  "Doctor",
  "HR",
  "Manager",
  "Receptionist",
  "Doctor Assistant",
  "Nurse",
  "Lab Technician",
  "Phlebotomist",
  "Pharmacist",
  "Accountant",
  "Social Media Manager",
  "Cleaning Staff",
  "Service Staff",
  "Guard",
  "Helper",
  "Driver",
  "Other",
];

const departments = [
  "OPD",
  "Doctors",
  "Reception",
  "Lab",
  "Pathology",
  "Radiology",
  "Nursing",
  "Pharmacy",
  "Accounts",
  "HR",
  "Administration",
  "Marketing",
  "Housekeeping",
  "Security",
  "Maintenance",
  "Ambulance",
  "Other",
];

function EmployeeQrCard({ employee }) {
  if (!employee) return null;

  const printCard = () => {
    window.print();
  };

  return (
    <div className="space-y-4">
      <div className="mx-auto w-full max-w-sm rounded-xl border border-gray-200 bg-white p-5 text-center shadow-sm">
        <p className="text-xs font-semibold uppercase tracking-widest text-hospital-text-muted">{CLINIC_NAME}</p>
        <h3 className="mt-2 font-manrope text-xl font-bold text-hospital-text-main">{employee.name}</h3>
        <p className="text-sm text-hospital-text-muted">
          {employee.employee_uid} | {employee.department}
        </p>
        {employee.photo_url && (
          <img src={employee.photo_url} alt={employee.name} className="mx-auto mt-4 h-20 w-20 rounded-full border border-gray-200 object-cover" />
        )}
        <div className="my-5 flex justify-center rounded-lg bg-white p-3">
          <QRCodeSVG value={employee.qr_code_id} size={190} level="M" includeMargin />
        </div>
        <p className="font-mono text-xs text-hospital-text-muted">{employee.qr_code_id}</p>
        {employee.username && (
          <div className="mt-4 rounded-lg bg-hospital-primary-light p-3 text-left text-sm">
            <p className="font-semibold text-hospital-primary">Employee Login</p>
            <p className="text-hospital-text-secondary">Username: {employee.username}</p>
          </div>
        )}
      </div>
      <Button className="btn-primary w-full" onClick={printCard}>
        <Printer className="mr-2 h-4 w-4" />
        Print QR Card
      </Button>
    </div>
  );
}

export default function Employees() {
  const [employees, setEmployees] = useState([]);
  const [open, setOpen] = useState(false);
  const [qrEmployee, setQrEmployee] = useState(null);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const load = () =>
    erpApi
      .employees()
      .then((res) => setEmployees(res.data))
      .catch(() => toast.error("Failed to load employees"));

  useEffect(() => {
    load();
  }, []);

  const submit = async (event) => {
    event.preventDefault();
    try {
      const schedulePayload = {
        working_days: form.working_days,
        shift_start: form.shift_start,
        shift_end: form.shift_end,
        grace_minutes: Number(form.grace_minutes || 0),
        full_day_hours: Number(form.full_day_hours || 0),
        half_day_hours: Number(form.half_day_hours || 0),
      };
      let response;
      if (editingEmployee) {
        response = await erpApi.updateEmployee(editingEmployee.id, {
          name: form.name,
          role: form.role,
          department: form.department,
          joining_date: form.joining_date,
          salary: Number(form.salary),
          phone: form.phone,
          photo_url: form.photo_url,
          ...schedulePayload,
          active: true,
        });
        toast.success("Employee updated");
      } else {
        response = await erpApi.createEmployee({ ...form, ...schedulePayload, salary: Number(form.salary) });
        toast.success(form.create_login ? "Employee and login account created" : "Employee added");
        setQrEmployee(response.data);
      }
      setOpen(false);
      setEditingEmployee(null);
      setForm(emptyForm);
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save employee");
    }
  };

  const openAddEmployee = () => {
    setEditingEmployee(null);
    setForm(emptyForm);
    setOpen(true);
  };

  const openEditEmployee = (employee) => {
    setEditingEmployee(employee);
    setForm({
      name: employee.name || "",
      role: employee.role || "",
      department: employee.department || "",
      photo_url: employee.photo_url || "",
      joining_date: employee.joining_date || "",
      salary: String(employee.salary || ""),
      phone: employee.phone || "",
      working_days: employee.working_days || ["mon", "tue", "wed", "thu", "fri", "sat"],
      shift_start: employee.shift_start || "09:00",
      shift_end: employee.shift_end || "18:00",
      grace_minutes: String(employee.grace_minutes ?? 15),
      full_day_hours: String(employee.full_day_hours ?? 8),
      half_day_hours: String(employee.half_day_hours ?? 4),
      create_login: false,
      username: employee.username || "",
      password: "",
    });
    setOpen(true);
  };

  const deleteEmployee = async (employee) => {
    const ok = window.confirm(`Delete employee "${employee.name}"?\n\nThis will also delete attendance, payroll and linked login account.`);
    if (!ok) return;
    try {
      await erpApi.deleteEmployee(employee.id);
      toast.success("Employee deleted");
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete employee");
    }
  };

  const regenerateQr = async (employee) => {
    if (!window.confirm(`Generate new QR card for ${employee.name}?\n\nOld QR will stop working.`)) return;
    try {
      const response = await erpApi.generateEmployeeQr(employee.id);
      toast.success("New QR generated");
      setQrEmployee(response.data);
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate QR");
    }
  };

  const toggleWorkingDay = (day) => {
    const exists = form.working_days.includes(day);
    setForm({
      ...form,
      working_days: exists ? form.working_days.filter((item) => item !== day) : [...form.working_days, day],
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col justify-between gap-4 sm:flex-row">
        <div>
          <h1 className="font-manrope text-2xl font-bold">Employees</h1>
          <p className="text-sm text-hospital-text-muted">
            Staff profiles, employee login accounts, salary details and QR attendance cards.
          </p>
        </div>
        <Dialog open={open} onOpenChange={(nextOpen) => { setOpen(nextOpen); if (!nextOpen) { setEditingEmployee(null); setForm(emptyForm); } }}>
          <Button className="btn-primary" onClick={openAddEmployee}>
              <Plus className="mr-2 h-4 w-4" />
              Add Employee
            </Button>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editingEmployee ? "Edit Employee" : "Add Employee"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={submit} className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {["name", "joining_date", "salary", "phone"].map((field) => (
                <div key={field} className="space-y-2">
                  <Label className="capitalize">{field.replace("_", " ")}</Label>
                  <Input
                    type={field === "joining_date" ? "date" : field === "salary" ? "number" : "text"}
                    value={form[field]}
                    onChange={(e) => setForm({ ...form, [field]: e.target.value })}
                  />
                </div>
              ))}
              <div className="space-y-2">
                <Label>Role</Label>
                <Select value={form.role} onValueChange={(value) => setForm({ ...form, role: value })}>
                  <SelectTrigger><SelectValue placeholder="Select employee role" /></SelectTrigger>
                  <SelectContent>
                    {employeeRoles.map((role) => <SelectItem key={role} value={role}>{role}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Department</Label>
                <Select value={form.department} onValueChange={(value) => setForm({ ...form, department: value })}>
                  <SelectTrigger><SelectValue placeholder="Select department" /></SelectTrigger>
                  <SelectContent>
                    {departments.map((department) => <SelectItem key={department} value={department}>{department}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <ProfilePhotoInput
                value={form.photo_url}
                onChange={(photoUrl) => setForm({ ...form, photo_url: photoUrl })}
                label="Employee Photo"
                className="sm:col-span-2"
              />

              <div className="space-y-3 rounded-lg border border-gray-200 p-3 sm:col-span-2">
                <div>
                  <Label>Working Days</Label>
                  <p className="text-xs text-hospital-text-muted">Employee kis din duty par aata hai select karein.</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {weekDays.map((day) => (
                    <Button
                      key={day.value}
                      type="button"
                      variant={form.working_days.includes(day.value) ? "default" : "outline"}
                      className={form.working_days.includes(day.value) ? "btn-primary" : ""}
                      onClick={() => toggleWorkingDay(day.value)}
                    >
                      {day.label}
                    </Button>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Shift Start</Label>
                <Input type="time" value={form.shift_start} onChange={(e) => setForm({ ...form, shift_start: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Shift End</Label>
                <Input type="time" value={form.shift_end} onChange={(e) => setForm({ ...form, shift_end: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Grace Minutes</Label>
                <Input type="number" value={form.grace_minutes} onChange={(e) => setForm({ ...form, grace_minutes: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Full Day Hours</Label>
                <Input type="number" step="0.5" value={form.full_day_hours} onChange={(e) => setForm({ ...form, full_day_hours: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Half Day Below Hours</Label>
                <Input type="number" step="0.5" value={form.half_day_hours} onChange={(e) => setForm({ ...form, half_day_hours: e.target.value })} />
              </div>

              {!editingEmployee && (
                <div className="flex items-center gap-2 rounded-lg border border-gray-200 p-3 sm:col-span-2">
                  <Checkbox
                    checked={form.create_login}
                    onCheckedChange={(checked) => setForm({ ...form, create_login: Boolean(checked) })}
                  />
                  <div>
                    <Label>Create employee login account</Label>
                    <p className="text-xs text-hospital-text-muted">Employee can sign in with this account later.</p>
                  </div>
                </div>
              )}

              {!editingEmployee && form.create_login && (
                <>
                  <div className="space-y-2">
                    <Label>Username</Label>
                    <Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} />
                  </div>
                  <div className="space-y-2">
                    <Label>Password</Label>
                    <PasswordInput autoComplete="new-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
                  </div>
                </>
              )}

              <div className="flex justify-end gap-2 sm:col-span-2">
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                  Cancel
                </Button>
                <Button className="btn-primary">{editingEmployee ? "Update Employee" : "Save & Generate QR"}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <DataTable
        title="Employee Registry"
        rows={employees}
        empty="No employees"
        columns={[
          { key: "employee_uid", label: "ID" },
          {
            key: "name",
            label: "Name",
            render: (row) => (
              <div className="flex items-center gap-3">
                {row.photo_url ? (
                  <img src={row.photo_url} alt={row.name} className="h-10 w-10 rounded-full object-cover" />
                ) : (
                  <div className="h-10 w-10 rounded-full bg-hospital-primary-light" />
                )}
                <span className="font-semibold text-hospital-text-main">{row.name}</span>
              </div>
            ),
          },
          { key: "role", label: "Role" },
          { key: "department", label: "Department" },
          { key: "shift_start", label: "Working Time", render: (row) => `${(row.working_days || []).join(", ").toUpperCase()} ${row.shift_start || "09:00"}-${row.shift_end || "18:00"}` },
          { key: "salary", label: "Salary", align: "right", render: (row) => formatCurrency(row.salary) },
          { key: "username", label: "Login", render: (row) => row.username || "-" },
          {
            key: "qr_code_id",
            label: "QR",
            render: (row) => (
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setQrEmployee(row)}>
                  <IdCard className="mr-2 h-4 w-4" />
                  QR Card
                </Button>
                <Button variant="outline" size="sm" onClick={() => regenerateQr(row)}>
                  New QR
                </Button>
              </div>
            ),
          },
          { key: "active", label: "Status", render: (row) => (row.active ? "Active" : "Inactive") },
          {
            key: "actions",
            label: "Actions",
            render: (row) => (
              <div className="flex gap-1">
                <Button variant="ghost" size="icon" title="Edit employee" onClick={() => openEditEmployee(row)}>
                  <Pencil className="h-4 w-4 text-hospital-primary" />
                </Button>
                <Button variant="ghost" size="icon" title="Delete employee" onClick={() => deleteEmployee(row)}>
                  <Trash2 className="h-4 w-4 text-red-600" />
                </Button>
              </div>
            ),
          },
        ]}
      />

      <Dialog open={Boolean(qrEmployee)} onOpenChange={(nextOpen) => !nextOpen && setQrEmployee(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Employee QR Card</DialogTitle>
          </DialogHeader>
          <EmployeeQrCard employee={qrEmployee} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
